// MyWorkspace.tsx

import React, { useState, useEffect, ReactElement } from 'react';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { actions as locationTaskActions } from "../../actions/locationTaskActions";
import { actions as myWorkspaceActions } from "../../actions/myWorkspaceActions";
import { actions as filesActions } from '../../actions/filesActions';
import { useDispatch } from "react-redux";
import type { Dispatch } from "redux";
import * as types from "../../constants/actionTypes";
import Notification from "../Common/Notification";
import { DATE_FORMAT, MOBILE_WINDOW_BREAKPOINT } from '../../constants/constants';
import TaskDetailManager from '../TaskList/TaskDetail/TaskDetailManager';
import Moment from 'moment';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { Button } from "primereact/button";

import {
    GlobalStyle,
    PageWrapper,
    Container,
    ColumnWrapper,
    ColumnHeader,
    CardWrapper,
    CardTitle,
    CardText,
    WorkspaceTitle,
    CardDate
} from './styles';
import LocationTaskSidebar from '@Components/TaskList/TaskDetail/LocationTaskSidebar/LocationTaskSidebar';

const PAGINATION_NUMBER = 20;

const MyWorkspace = (props: any): ReactElement => {
    const dispatch: Dispatch = useDispatch();
    const [tasks, setTasks] = useState([]);
    const [isTaskDetailOpen, setIsTaskDetailOpen] = useState(false);
    const [locationUsers, setLocationUsers] = useState([]);
    const [editLocationKey, setEditLocationKey] = useState<string>('');
    const [editTaskId, setEditTaskId] = useState<number>(0);

    const [columns, setColumns] = useState({});

    const onDragEnd = async (result) => {
        // Handle task reordering on drag-and-drop
        const { destination, source, draggableId } = result;
        if (!destination) return; // Drop outside a droppable area
        if (source.droppableId == destination.droppableId) return;

        // Clone the columns object
        const updatedColumns = { ...columns };

        // Retrieve source and destination columns
        const sourceColumn = updatedColumns[source.droppableId];
        const destinationColumn = updatedColumns[destination.droppableId];

        // Clone the source and destination columns
        const updatedSourceColumn = { ...sourceColumn };
        const updatedDestinationColumn = { ...destinationColumn };

        // Update taskIds in the source column
        updatedSourceColumn.taskIds = sourceColumn.taskIds.filter((taskId) => taskId !== draggableId);

        // Update taskIds in the destination column
        updatedDestinationColumn.taskIds.splice(destination.index, 0, draggableId);

        // Update the cloned columns object with modified source and destination columns
        updatedColumns[source.droppableId] = updatedSourceColumn;
        updatedColumns[destination.droppableId] = updatedDestinationColumn;

        // Set the modified columns
        setColumns(updatedColumns);

        // Perform the necessary status update based on the column
        const updatedTasks = tasks.map((task) =>
            task.id === draggableId ? { ...task, status: destinationColumn.id } : task
        );

        setTasks(updatedTasks);

        try {
            const result = await updateStatus([draggableId], destinationColumn.id);
            const payload = result.payload;
            if (result.type === types.UPDATE_LOCATION_TASK_LIST_STATUS_SUCCESS) {
                Notification(
                    "success",
                    "",
                    "The Task was moved."
                );
            } else if (result.type === types.UPDATE_LOCATION_TASK_LIST_STATUS_FAILURE) {
                Notification("error", "An Error Occurred", payload.message);
                await rollbackTaskIfErrorOccurs(destination, source, draggableId);
            }
        } catch (error) {
            Notification("error", "Error", error);
            await rollbackTaskIfErrorOccurs(destination, source, draggableId);
        }
    };

    const rollbackTaskIfErrorOccurs = async (destination, source, draggableId) => {
        // Clone the columns object
        const updatedColumns = { ...columns };

        // Retrieve source and destination columns
        const destinationColumn = updatedColumns[destination.droppableId];

        // Clone the source and destination columns
        const updatedDestinationColumn = { ...destinationColumn };

        // Update taskIds in the destination column
        updatedDestinationColumn.taskIds = destinationColumn.taskIds.filter((taskId) => taskId !== draggableId);

        // Update the cloned columns object with modified source and destination columns
        updatedColumns[destination.droppableId] = updatedDestinationColumn;

        // Set the modified columns
        setColumns(updatedColumns);

        // Perform the necessary status update based on the column
        const updatedTasks = tasks.map((task) =>
            task.id === draggableId ? { ...task, status: source.id } : task
        );

        setTasks(updatedTasks);
    };

    const updateStatus = async (ids, statusId) =>
        dispatch(locationTaskActions.updateStatus(ids, statusId));

    const loadTaskColumnStatuses = async () =>
        dispatch(myWorkspaceActions.loadDefaultStatuses());

    const searchResponsibleUsers = async (locKey: string) =>
        dispatch(locationTaskActions.searchResponsibleUsers([locKey]));

    const loadMyTasksByStatus = async (statusId: number, pageNumber: number) => {
        var params = {
            statusId: statusId,
            pageNumber: pageNumber,
            pageSize: PAGINATION_NUMBER
        }

        return dispatch(myWorkspaceActions.myTasksByStatus(params));
    }

    const formatTasksByStatus = async (statusId: number, pageNumber: number) => {
        try {
            const result = await loadMyTasksByStatus(statusId, pageNumber);
            const payload = result.payload;
            if (result.type === types.MY_WORKSPACE_MY_TASKS_BY_STATUS_SUCCESS) {

                setTasks(prevTasks => {
                    const updatedTasks = [...prevTasks];
                    payload.results.forEach(newTask => {
                        const index = updatedTasks.findIndex(task => task.id === newTask.id);
                        if (index !== -1) {
                            // Task exists, update its attributes
                            updatedTasks[index] = newTask;
                        } else {
                            // Task doesn't exist, insert it into the array
                            updatedTasks.push(newTask);
                        }
                    });
                    return updatedTasks;
                });

                return payload;
            } else if (result.type === types.MY_WORKSPACE_MY_TASKS_BY_STATUS_FAILURE) {
                Notification("error", "An Error Occurred", payload.message);
            }
        } catch (error) {
            Notification("error", "Error", error);
        }
    }

    const formatStatus = async () => {
        try {
            const result = await loadTaskColumnStatuses();
            const payload = result.payload;

            if (result.type === types.MY_WORKSPACE_LOAD_DEFAULT_STATUSES_SUCCESS) {
                const transformedData = {};
                const sortedStatuses = payload.statuses;

                // Create an array of promises to fetch taskIds for each status
                const statusPromises = sortedStatuses.map(async (item) => {
                    let color = '';

                    if (item.coreStatusId === 1) {
                        color = '#FAFAFA';
                    } else if (item.coreStatusId === 3) {
                        color = '#F7F7FE';
                    } else if (item.coreStatusId === 4) {
                        color = '#E4F8F0';
                    }

                    // Fetch taskIds asynchronously
                    const tasks = await formatTasksByStatus(item.id, 1);

                    transformedData[item.id] = {
                        id: item.id.toString(),
                        title: item.name,
                        coreStatusId: item.coreStatusId,
                        color: color,
                        taskIds: tasks.results.map(item => String(item.id)),
                        pageNumber: 1,
                        showLoadMore: tasks.totalCount > PAGINATION_NUMBER
                    };
                });

                // Wait for all promises to resolve
                await Promise.all(statusPromises);

                return transformedData;
            } else if (result.type === types.MY_WORKSPACE_LOAD_DEFAULT_STATUSES_FAILURE) {
                Notification("error", "An Error Occurred", payload.message);
            }
        } catch (error) {
            Notification("error", "Error", error);
        }
    };

    const hideColumn = (columnId) => {
        const selectedColumn = { ...columns };
        selectedColumn[columnId].disabled = !selectedColumn[columnId].disabled;
        setColumns(selectedColumn);
    }

    const isMobile = () => {
        return props.windowWidth < MOBILE_WINDOW_BREAKPOINT;
    }

    const onTaskDetailCancel = () => {
        setIsTaskDetailOpen(false);
    }

    const onTaskDetailComplete = () => {
        setIsTaskDetailOpen(false);
    }

    const selectTask = async (task) => {
        try {
            const response = await searchResponsibleUsers(task.locationKey);
            if (response.type === types.SEARCH_LOCATION_TASK_RESPONSIBLE_USERS_SUCCESS) {
                setLocationUsers(response.payload.users);
                setEditTaskId(task.id);
                setEditLocationKey(task.locationKey);
                setIsTaskDetailOpen(true);
            } else if (response.type === types.SEARCH_LOCATION_TASK_RESPONSIBLE_USERS_FAILURE) {
                Notification("error", "Error", response.payload.message);
            }
        } catch (error) {
            Notification("error", "Error", error);
        }
    }

    const diffDays = (date) => {
        return Moment(date).diff(Moment(), 'days');
    }

    const loadMore = async (statusId: number, pageNumber: number) => {
        // Fetch taskIds asynchronously
        const tasks = await formatTasksByStatus(statusId, pageNumber);

        // Clone the columns object
        const updatedColumns = { ...columns };

        // Retrieve source and destination columns
        const destinationColumn = updatedColumns[statusId];

        // Clone the source and destination columns
        const updatedDestinationColumn = { ...destinationColumn };

        // Update taskIds in the destination column
        updatedDestinationColumn.taskIds = destinationColumn.taskIds.concat(tasks.results.map(item => String(item.id)));
        updatedDestinationColumn.showLoadMore = (pageNumber * PAGINATION_NUMBER) < tasks.totalCount;
        updatedDestinationColumn.pageNumber = pageNumber;

        // Update the cloned columns object with modified source and destination columns
        updatedColumns[statusId] = updatedDestinationColumn;

        // Set the modified columns
        setColumns(updatedColumns);
    }

    useEffect(() => {
        const fetchData = async () => {
            const newStatuses = await formatStatus();

            // Set the state with the new statuses, replacing the existing state
            setColumns(newStatuses);
        };

        fetchData();
    }, [isTaskDetailOpen]);

    return (
        <>
            <GlobalStyle />
            <PageWrapper>
                <WorkspaceTitle>My Workspace</WorkspaceTitle>
                <DragDropContext onDragEnd={onDragEnd}>
                    <Container>
                        {Object.values(columns).sort((a, b) => a.coreStatusId - b.coreStatusId).map((column) => (
                            <div key={column.id}>
                                <Droppable droppableId={column.id} isDropDisabled={column.disabled}>
                                    {(provided) => (
                                        <ColumnWrapper
                                            {...provided.droppableProps}
                                            ref={provided.innerRef}
                                            color={column.color}
                                            disabled={column.disabled}
                                        >
                                            <ColumnHeader disabled={column.disabled}>{column.title}<i className={column.disabled ? 'pi pi-chevron-right' : 'pi pi-chevron-left'} onClick={() => hideColumn(column.id)}></i></ColumnHeader>
                                            {column.taskIds.map((taskId, index) => {
                                                const task = tasks.find((t) => t.id.toString() === taskId);
                                                return (
                                                    <Draggable key={taskId} draggableId={taskId} index={index}>
                                                        {(provided) => (
                                                            <CardWrapper
                                                                ref={provided.innerRef}
                                                                {...provided.draggableProps}
                                                                {...provided.dragHandleProps}
                                                                onClick={() => selectTask(task)}
                                                            >
                                                                <CardTitle>{task?.name}</CardTitle>
                                                                <CardText>
                                                                    {`${task?.projectAbbreviation} | ${task?.locationAbbreviation}`}
                                                                </CardText>
                                                                <CardDate color={column.coreStatusId !== 4 && diffDays(task.targetDate) < 0 ? 'red' : ''}>
                                                                    {task?.startDate && (
                                                                        <div>
                                                                            <span>{Moment(task.startDate).format(DATE_FORMAT)}</span>
                                                                            <span>Start Date</span>
                                                                        </div>
                                                                    )}
                                                                    <div className="target-date">
                                                                        <span>{Moment(task.targetDate).format(DATE_FORMAT)}</span><span>Target Date</span>
                                                                    </div>
                                                                    <div className="time-remaining">
                                                                        <span>
                                                                            {diffDays(task.targetDate)}{' '}{task.timeRemaining === 1 ? 'Day' : 'Days'}
                                                                        </span>
                                                                        <span>Time Remaining</span>
                                                                    </div>
                                                                </CardDate>
                                                            </CardWrapper>
                                                        )}
                                                    </Draggable>
                                                );
                                            })}
                                            {provided.placeholder}
                                            {
                                                column.showLoadMore && <Button label="Load More" onClick={() => loadMore(column.id, column.pageNumber + 1)} />
                                            }
                                        </ColumnWrapper>
                                    )}
                                </Droppable>
                            </div>
                        ))}
                    </Container>
                </DragDropContext>
            </PageWrapper>
            {
                isTaskDetailOpen &&
                <LocationTaskSidebar
                    locationTaskId={editTaskId}
                    changeTask={setEditTaskId}
                    locationKey={editLocationKey}
                    isDrawerVisible={isTaskDetailOpen}
                    responsibleUsers={locationUsers}
                    closeDrawer={onTaskDetailCancel}
                    onComplete={onTaskDetailComplete}
                    isMobile={isMobile()}
                />
            }
        </>
    );
};

// export default MyWorkspace;

function mapStateToProps(state) {
    return {
        userRole: state.user.customerRole,
        userId: state.user.id
    };
}

const mapDispatchToProps = dispatch => {
    return {
        filesActions: bindActionCreators(filesActions, dispatch),
        locationTaskActions: bindActionCreators(locationTaskActions, dispatch)
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(MyWorkspace);